import React from "react";
import { useNavigate } from "react-router-dom";
import { FeatherAlertCircle, FeatherRefreshCw } from "@subframe/core";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { SettingsManagedPanel } from "../components/SettingsManagedPanel";
import { SettingsLayout } from "../layout/SettingsLayout";
import { useLocationsPanel } from "../hooks/useLocationsPanel";
import type { CompanyLocationDTO } from "../../data/models/CompanySettingsDTO";

const Th: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <th className="border-b border-solid border-neutral-border px-3 py-2 text-left text-caption-bold font-caption-bold uppercase text-subtext-color">
    {children}
  </th>
);

const Td: React.FC<{ children?: React.ReactNode; muted?: boolean }> = ({
  children,
  muted,
}) => (
  <td
    className={`px-3 py-2.5 align-middle text-body font-body ${
      muted ? "text-subtext-color" : "text-default-font"
    }`}
  >
    {children}
  </td>
);

/** `2026-09-11 17:11:14` -> `11 Sep 2026, 17:11`. Anything unparseable passes through. */
const formatSyncedAt = (value: string | null): string => {
  if (!value) return "Never";

  const parsed = new Date(value.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;

  return `${parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}, ${parsed.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })}`;
};

/**
 * ZOUR-92 — the one editable cell on this page.
 *
 * Everything else in the table is Zoho's and is pulled; this column is
 * Zournal's, for the same reason `company_profile.gst_enabled_from` is: Zoho
 * does not enforce its own registration date, so it cannot be what we enforce.
 *
 * BLANK MEANS INHERIT, NOT "ALWAYS". That distinction is the whole reason the
 * helper line under the field says what will actually be used — a blank field
 * beside a company date of 2026-06-01 is not "GST from the beginning of time",
 * it is "2026-06-01", and a reader cannot be left to guess which.
 *
 * Local draft state, committed on blur or Save rather than on every keystroke:
 * this writes to the database and re-reads the whole panel.
 */
const GstDateCell: React.FC<{
  location: CompanyLocationDTO;
  saving: boolean;
  onSave: (id: number, value: string | null) => void;
}> = ({ location, saving, onSave }) => {
  const [draft, setDraft] = React.useState(location.gst_enabled_from ?? "");

  React.useEffect(() => {
    setDraft(location.gst_enabled_from ?? "");
  }, [location.gst_enabled_from]);

  const dirty = draft !== (location.gst_enabled_from ?? "");
  const canSave = location.id !== null && dirty && !saving;

  const inherited =
    (location.gst_enabled_from ?? "") === "" &&
    location.effective_gst_enabled_from !== null;

  return (
    <td className="px-3 py-2.5 align-middle">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={draft}
            disabled={location.id === null || saving}
            onChange={(event) => setDraft(event.target.value)}
            className="rounded-md border border-solid border-neutral-border bg-default-background px-2 py-1 text-body font-body text-default-font disabled:bg-neutral-50 disabled:text-subtext-color"
            aria-label={`GST applies from, ${location.name}`}
          />
          {canSave && (
            <Button size="small" onClick={() => onSave(location.id as number, draft === "" ? null : draft)}>
              Save
            </Button>
          )}
          {saving && (
            <span className="text-caption font-caption text-subtext-color">Saving…</span>
          )}
        </div>

        {inherited && (
          <span className="text-caption font-caption text-subtext-color">
            Using the company date ({location.effective_gst_enabled_from})
          </span>
        )}

        {!inherited && (location.gst_enabled_from ?? "") === "" && (
          <span className="text-caption font-caption text-subtext-color">
            GST applies to documents of every date
          </span>
        )}

        {location.zoho_registered_on && (
          <span className="text-caption font-caption text-subtext-color">
            Zoho Books says {location.zoho_registered_on}
            {location.zoho_registered_on !== (location.gst_enabled_from ?? "") && (
              <button
                type="button"
                className="ml-1 cursor-pointer text-brand-primary underline"
                onClick={() => setDraft(location.zoho_registered_on ?? "")}
              >
                use it
              </button>
            )}
          </span>
        )}
      </div>
    </td>
  );
};

/**
 * ZOUR-92 addendum (2026-09-15) — the primary registration's cell, read-only.
 *
 * ONE FACT, ONE HOME. The company's "GST applies from" date on `/settings/gst`
 * has always been the primary registration's date — before ZOUR-65 the company
 * had exactly that one GSTIN. Drawing an editable field for it here as well made
 * one date look like two settings and let them contradict each other, which is
 * how the first reader of this page read it. So the primary says what is in
 * force and points at the page that owns it.
 *
 * No Zoho hint either, by the user's instruction: the "use it" action exists to
 * fill an input, and there is no input to fill. `zoho_registered_on` is still
 * synced and stored for this row — it is simply not shown here.
 *
 * Branch rows keep `GstDateCell` exactly as it was.
 */
const PrimaryGstDateCell: React.FC<{
  location: CompanyLocationDTO;
}> = ({ location }) => {
  // Routed, not an `<a href>`: this is a move between two panels of the same
  // settings screen, and a full page reload would throw away the panel state
  // the rest of the rail keeps.
  const navigate = useNavigate();

  return (
    <td className="px-3 py-2.5 align-middle">
      <div className="flex flex-col gap-1">
        <span className="text-body font-body text-default-font">
          {location.effective_gst_enabled_from
            ? `Company date (${location.effective_gst_enabled_from})`
            : "GST applies to documents of every date"}
        </span>
        <button
          type="button"
          className="w-fit cursor-pointer text-caption font-caption text-brand-primary underline"
          onClick={() => navigate("/settings/gst")}
        >
          Set on the GST page
        </button>
      </div>
    </td>
  );
};

const LocationRow: React.FC<{
  location: CompanyLocationDTO;
  saving: boolean;
  showGstDate: boolean;
  onSaveGstDate: (id: number, value: string | null) => void;
}> = ({ location, saving, showGstDate, onSaveGstDate }) => (
  <tr
    className={`border-t border-solid border-neutral-border ${
      location.is_active ? "" : "bg-neutral-50 opacity-70"
    }`}
  >
    <Td>
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-body-bold font-body-bold text-default-font">
          {location.name}
        </span>
        {location.is_primary && <Badge variant="neutral">Primary</Badge>}
      </span>
    </Td>
    <Td>
      {location.gstin ? (
        <span className="text-monospace-body font-monospace-body">{location.gstin}</span>
      ) : (
        <span className="text-subtext-color">Not set in Zoho</span>
      )}
    </Td>
    <Td muted>{location.state ?? "—"}</Td>
    <Td>
      {location.is_active ? (
        <Badge variant="success">Active</Badge>
      ) : (
        <Badge variant="neutral">Inactive</Badge>
      )}
    </Td>
    {showGstDate &&
      (location.is_primary ? (
        <PrimaryGstDateCell location={location} />
      ) : (
        <GstDateCell location={location} saving={saving} onSave={onSaveGstDate} />
      ))}
    <Td muted>{location.series_name ?? "—"}</Td>
    <Td muted>{formatSyncedAt(location.synced_at)}</Td>
  </tr>
);

const PanelSkeleton: React.FC = () => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center py-16">
    <div className="flex flex-col items-center gap-4">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
      <span className="text-body font-body text-subtext-color">Loading locations...</span>
    </div>
  </div>
);

const PanelError: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center py-16">
    <div className="flex max-w-md flex-col items-center gap-4 text-center">
      <FeatherAlertCircle className="text-heading-1 font-heading-1 text-error-600" />
      <span className="text-heading-3 font-heading-3 text-default-font">
        We couldn't load your GST registrations
      </span>
      <span className="text-body font-body text-subtext-color">{message}</span>
      <Button onClick={onRetry}>Try again</Button>
    </div>
  </div>
);

/**
 * LocationsPage — `/settings/locations`.
 *
 * ZOUR-65 phase 1. A Zoho Books organisation can hold several GST
 * registrations, one per *Location* (Zoho's word; it used to say Branch). This
 * is the list, and it is the first screen in Zournal that admits a company can
 * have more than one GSTIN.
 *
 * READ-ONLY, and not as a simplification. Zoho's `POST locations` needs a
 * `tax_settings_id` that only Zoho mints when a GSTIN is added through its own
 * GST settings screen, so a location genuinely cannot be created from here.
 * Enabling Locations in Zoho is also irreversible, which is a decision that
 * should be taken in Zoho with Zoho's own warning in front of you, not behind a
 * button on this page. So: show the list and offer a Refresh — the same shape and
 * the same reasoning as `GstZohoBlock`. (The line naming Zoho Books → Settings →
 * Organisation → Locations as where they are managed was removed on the user's
 * instruction, 2026-09-15; the reasoning above is why the page is read-only, not
 * a promise that the page still says so.)
 *
 * The phase-1 promise is that NOTHING about a document changes. Picking a
 * location on a bill or an invoice is phase 2; matching an OCR'd buyer GSTIN to
 * one of these registrations is phase 3.
 */
export const LocationsPage: React.FC = () => {
  const {
    data,
    loading,
    error,
    refreshing,
    refreshError,
    refreshNote,
    refresh,
    reload,
    savingGstDateFor,
    gstDateError,
    gstDateNote,
    saveGstDate,
  } = useLocationsPanel();

  /**
   * ZOUR-92 — the per-registration date column is for companies that HAVE
   * several registrations.
   *
   * With one active registration there is nothing to vary: that registration's
   * date IS the company's, and it is already set on the GST panel. Showing a
   * second field for the same fact would be two places to set one date and a
   * way to make them contradict each other — so a single-registration tenant
   * sees exactly the table it saw before this ticket. Same sentinel discipline
   * as the location flags and the document picker.
   */
  const showGstDateColumn = (data?.active_count ?? 0) > 1;

  return (
    <SettingsLayout>
      <SettingsManagedPanel
        title="Locations"
        description="The GST registrations your books are kept under. One per location in Zoho Books."
      >
        {loading ? (
          <PanelSkeleton />
        ) : error ? (
          <PanelError message={error} onRetry={() => void reload()} />
        ) : (
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  {data?.count ?? 0} location{(data?.count ?? 0) === 1 ? "" : "s"}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Last read from Zoho Books: {formatSyncedAt(data?.synced_at ?? null)}
                </span>
              </div>

              <Button
                variant="neutral-secondary"
                icon={<FeatherRefreshCw />}
                loading={refreshing}
                disabled={refreshing}
                onClick={() => void refresh()}
              >
                Refresh
              </Button>
            </div>

            {/* The fallback row is a guess, and saying so is the difference
                between "this is your registration" and "this is what we assume
                until we ask Zoho". A genuine single-location org looks
                identical in the table but is not guessing. */}
            {data?.not_synced && (
              <div className="flex w-full items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                <FeatherAlertCircle className="mt-0.5 shrink-0 text-body font-body text-subtext-color" />
                <span className="text-body font-body text-subtext-color">
                  Not synced yet — Refresh reads the list from Zoho Books. Until then this
                  shows the single GST registration on your company details.
                </span>
              </div>
            )}

            {refreshError && (
              <div className="flex w-full items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
                <FeatherAlertCircle className="mt-0.5 shrink-0 text-body font-body text-error-700" />
                <span className="text-body font-body text-error-700">
                  {refreshError} The list below is the last one we read.
                </span>
              </div>
            )}

            {refreshNote && !refreshError && (
              <span className="text-caption font-caption text-subtext-color">
                {refreshNote}
              </span>
            )}

            {/* ZOUR-92 — the save feedback for the one editable column. Kept
                beside the table rather than inside the cell so a long message
                cannot push the row layout around. */}
            {gstDateError && (
              <div className="flex w-full items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
                <FeatherAlertCircle className="mt-0.5 shrink-0 text-body font-body text-error-700" />
                <span className="text-body font-body text-error-700">{gstDateError}</span>
              </div>
            )}

            {gstDateNote && !gstDateError && (
              <span className="text-caption font-caption text-subtext-color">
                {gstDateNote}
              </span>
            )}

            <div className="w-full overflow-x-auto rounded-md border border-solid border-neutral-border bg-default-background">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-neutral-50">
                    <Th>Location</Th>
                    <Th>GSTIN</Th>
                    <Th>State</Th>
                    <Th>Status</Th>
                    {showGstDateColumn && <Th>GST applies from *</Th>}
                    <Th>Series</Th>
                    <Th>Last synced</Th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.locations ?? []).map((location, index) => (
                    <LocationRow
                      key={location.zoho_location_id ?? `fallback-${index}`}
                      location={location}
                      saving={savingGstDateFor === location.id}
                      showGstDate={showGstDateColumn}
                      onSaveGstDate={saveGstDate}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* The star ties this line to the column header rather than leaving a
                paragraph of small print floating under an unrelated table. It is
                shown and hidden with the column it explains. */}
            {showGstDateColumn && (
              <span className="text-caption font-caption text-subtext-color">
                * <strong>GST applies from</strong> is Zournal's, not Zoho's: documents filed
                under a registration and dated before it keep their GSTIN but are pushed
                without tax. The primary registration always uses the company date from the
                GST page; leave a branch blank to inherit it.
              </span>
            )}
          </div>
        )}
      </SettingsManagedPanel>
    </SettingsLayout>
  );
};

export default LocationsPage;
